import type { Probe, Finding } from '../engine/types';
import { ok } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const WEBDRIVER_SOURCE = 'https://w3c.github.io/webdriver/#webdriver-active-flag';
const CDP_SOURCE = 'https://chromedevtools.github.io/devtools-protocol/';

/**
 * Automation detection.
 *
 * Layer 1: direct artifacts (webdriver flag, leaked framework globals).
 * Layer 2: indirect artifacts (CDP stack frames, permission-query asymmetry).
 *
 * The permission asymmetry check is worth explaining: in a normal browser,
 * Notification.permission is 'default' while navigator.permissions.query for
 * 'notifications' reports the same, and the permissions API resolves. In many
 * automated contexts the query throws or resolves inconsistently with the
 * legacy property, because a headless/CDP profile patches one and not the
 * other. It is a weak signal on its own.
 */
export const automationProbe: Probe = {
  id: 'automation',
  label: 'Automation indicators',
  layer: 'browser',
  tier: 'client',
  category: 'browser',
  description: 'Direct and indirect browser-automation artifacts',
  budgetMs: 3000,

  async run() {
    const started = performance.now();
    const n = navigator as Navigator & { deviceMemory?: number };
    const findings: Finding[] = [];

    // --- direct: webdriver flag -------------------------------------------
    const webdriver = n.webdriver === true;
    if (webdriver) {
      findings.push({
        id: 'automation.webdriver',
        severity: 'high',
        category: 'browser',
        layer: 'browser',
        confidence: 'high',
        title: 'navigator.webdriver is true',
        detail:
          'The WebDriver active flag is set, which is a strong and direct indicator that this browser is being controlled through WebDriver (Selenium, Playwright, chromedriver, Puppeteer).',
        sources: [WEBDRIVER_SOURCE],
        evidence: ['navigator.webdriver === true'],
        scoreContribution: 50,
      });
    }

    // --- direct: leaked framework globals ---------------------------------
    const leaked = findLeakedGlobals();
    if (leaked.length > 0) {
      findings.push({
        id: 'automation.leaked-globals',
        severity: 'high',
        category: 'browser',
        layer: 'browser',
        confidence: 'high',
        title: `Automation framework globals present: ${leaked.join(', ')}`,
        detail:
          'Automation frameworks and some anti-detect browsers leave well-known globals on the page. A genuine browser does not define them.',
        sources: [CDP_SOURCE],
        evidence: leaked.map((k) => `window.${k} is defined`),
        scoreContribution: 40,
      });
    }

    // --- indirect: CDP stack artifact ------------------------------------
    const cdpStack = detectCdpStack();
    if (cdpStack.detected) {
      findings.push({
        id: 'automation.cdp-stack',
        severity: 'medium',
        category: 'browser',
        layer: 'browser',
        confidence: 'high',
        title: 'CDP-style execution frames present in the call stack',
        detail:
          'An Error stack captured in the page contains EvaluationScript/getOwnPropertyDescriptor-style frames. These are injected by the Chrome DevTools Protocol when a client evaluates expressions in the page, a hallmark of Puppeteer/Playwright even when navigator.webdriver is masked.',
        sources: [CDP_SOURCE],
        evidence: cdpStack.frames,
        scoreContribution: 20,
      });
    }

    // --- indirect: permission-query asymmetry -----------------------------
    const perm = await permissionAsymmetry();
    if (perm.asymmetric) {
      findings.push({
        id: 'automation.permission-asymmetry',
        severity: 'low',
        category: 'browser',
        layer: 'browser',
        confidence: 'low',
        title: 'Notification permission API is inconsistent with the legacy property',
        detail:
          'Notification.permission and navigator.permissions.query disagree, or the query failed. Common in automated profiles where one surface is patched but the other is not. Weak on its own, and it contributes no score.',
        sources: [WEBDRIVER_SOURCE],
        evidence: perm.evidence,
        scoreContribution: 0,
      });
    }

    // --- browser feature bitmask -----------------------------------------
    // Parity field: a compact bitmask of a handful of browser capabilities.
    const features = {
      chrome: hasChrome(),
      brave: hasBrave(),
      braveWebkit: 'brave' in navigator,
      webAssembly: typeof WebAssembly === 'object',
      usb: 'usb' in navigator,
      browserCapture: 'getDisplayMedia' in navigator,
      battery: 'getBattery' in navigator,
    };
    const bitmask = Object.values(features)
      .map((v) => (v ? '1' : '0'))
      .join('');

    return {
      signals: {
        webdriver: ok(webdriver, 'client', performance.now() - started),
        leakedGlobals: ok(leaked, 'client'),
        cdpStack: ok(cdpStack.detected, 'client'),
        cdpFrames: ok(cdpStack.frames, 'client'),
        permissionAsymmetry: ok(perm.asymmetric, 'client'),
        features: ok(features, 'client'),
        browserFeaturesBitmask: ok(bitmask, 'client'),
      },
      findings,
      parts: {
        // The bitmask is a stable capability signature; leaked globals and the
        // webdriver flag are deliberately excluded so that a masked
        // automation run does not change the device identity hash.
        browserFeatures: { bitmask },
      },
    };
  },
};

function findLeakedGlobals(): string[] {
  const candidates = [
    '__playwright__binding__',
    '__pw_manual',
    '__PUPPETEER_SCRIPT__',
    '_selenium',
    'callSelenium',
    '__webdriver_evaluate',
    '__selenium_evaluate',
    '__webdriver_script_function',
    '__driver_evaluate',
    '__fxdriver_evaluate',
    '__webdriver_unwrapped',
    '__driver_unwrapped',
    'callPhantom',
    '_phantom',
    '__nightmare',
    'domAutomation',
    '__puppeteer_evaluation_script__',
  ];
  const w = window as unknown as Record<string, unknown>;
  return candidates.filter((k) => w[k] !== undefined);
}

/**
 * Detect CDP-injected frames by capturing an Error stack inside an
 * Object.getOwnPropertyDescriptor access, which is a common interception point
 * for automation/stealth code.
 */
function detectCdpStack(): { detected: boolean; frames: string[] } {
  const frames: string[] = [];
  try {
    const err = new Error('stack-probe');
    const stack = err.stack ?? '';
    const suspicious =
      /EvaluationScript|getOwnPropertyDescriptor|__playwright|__puppeteer|__webdriver_evaluate|Function.prototype.apply/i;
    for (const line of stack.split('\n')) {
      if (suspicious.test(line)) frames.push(line.trim());
    }
  } catch {
    return { detected: false, frames: [] };
  }
  return { detected: frames.length > 0, frames: frames.slice(0, 5) };
}

async function permissionAsymmetry(): Promise<{ asymmetric: boolean; evidence: string[] }> {
  const evidence: string[] = [];
  if (!('Notification' in window) || !navigator.permissions?.query) {
    return { asymmetric: false, evidence: ['permissions API unavailable'] };
  }
  let legacy: string;
  try {
    legacy = Notification.permission;
  } catch {
    return { asymmetric: false, evidence: ['Notification.permission threw'] };
  }
  try {
    const status = await navigator.permissions.query({ name: 'notifications' as PermissionName });
    evidence.push(`Notification.permission=${legacy}`, `permissions.query=${status.state}`);
    // In a normal browser these agree. They differ only if one is patched.
    return { asymmetric: legacy !== status.state, evidence };
  } catch (err) {
    evidence.push(`permissions.query threw: ${String(err)}`);
    // A throwing query with a readable legacy property is itself a signal.
    return { asymmetric: true, evidence };
  }
}

function hasChrome(): boolean {
  return (
    'chrome' in window ||
    'webkitPersistentStorage' in navigator ||
    /Chrome/.test(navigator.userAgent)
  );
}

function hasBrave(): boolean {
  // Brave exposes a promise-based brave object; presence of the global is the tell.
  return 'brave' in (window as unknown as Record<string, unknown>);
}

registerProbe(automationProbe);
